import React, {useState, useEffect} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import css from './ImagesGallery.module.scss';
import ChevronLeft from '../Icons/ChevronLeft';
import ChevronRight from '../Icons/ChevronRight';
import X02 from '../Icons/X02';
import {colors} from "../../utils/colors.js";
import Button from "../Button/Button.jsx";
import ImageIcon from "../Icons/ImageIcon.jsx";

const ImagesGallery = ({
  images = [],
  imageVersion = '250s',
  fullscreenImageVersion = '640',
  alt = 'Gallery image',
  className = '',
  style = {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  
  if (!images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const imageUrl = currentImage?.versions?.[imageVersion] || null;
  const hasMultipleImages = images.length > 1;

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = () => {
    setFullscreenIndex(currentIndex);
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  const handleFullscreenPrevious = (e) => {
    e.stopPropagation();
    setFullscreenIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleFullscreenNext = (e) => {
    e.stopPropagation();
    setFullscreenIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    const handleArrowKeys = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setFullscreenIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setFullscreenIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('keydown', handleArrowKeys);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('keydown', handleArrowKeys);
      document.body.style.overflow = '';
    };
  }, [isFullscreen, images.length]);

  if (!imageUrl) {
    return null;
  }

  const fullscreenImage = images[fullscreenIndex];
  const fullscreenImageUrl = fullscreenImage?.versions?.[fullscreenImageVersion] || 
                             fullscreenImage?.versions?.['640'] || 
                             fullscreenImage?.versions?.[imageVersion] || null;

  return (
    <>
      <div className={`${css.imagesGallery} ${className}`} style={style}>
        <div className={css.imageContainer}>
          <img
            src={imageUrl}
            alt={alt}
            className={css.image}
            onClick={handleImageClick}
          />
          {hasMultipleImages &&
            <>
              <button
                type="button"
                className={css.navButton}
                onClick={handlePrevious}
                aria-label="Previous image"
              >
                <ChevronLeft color={colors.primaryColor} />
              </button>
              <button
                type="button"
                className={`${css.navButton} ${css.navButtonRight}`}
                onClick={handleNext}
                aria-label="Next image"
              >
                <ChevronRight color={colors.primaryColor} />
              </button>
              <div className={css.imageCounter}>
                <ImageIcon /> {currentIndex + 1} / {images.length}
              </div>
            </>
          }
        </div>
      </div>
      {isFullscreen && fullscreenImageUrl && createPortal(
        <div className={css.fullscreenOverlay} onClick={handleCloseFullscreen}>
          <div className={css.fullscreenContainer} onClick={(e) => e.stopPropagation()}>
            <div
              className={css.fullscreenClose}
              onClick={handleCloseFullscreen}
            >
              <X02 color={colors.white} />
            </div>
            {hasMultipleImages &&
              <>
                <button
                  type="button"
                  className={css.fullscreenNavButton}
                  onClick={handleFullscreenPrevious}
                  aria-label="Previous image"
                >
                  <ChevronLeft />
                </button>
                <button
                  type="button"
                  className={`${css.fullscreenNavButton} ${css.fullscreenNavButtonRight}`}
                  onClick={handleFullscreenNext}
                  aria-label="Next image"
                >
                  <ChevronRight />
                </button>
                <div className={css.fullscreenCounter}>
                  {fullscreenIndex + 1} / {images.length}
                </div>
              </>
            }
            <img
              src={fullscreenImageUrl}
              alt={alt}
              className={css.fullscreenImage}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

ImagesGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      versions: PropTypes.objectOf(PropTypes.string),
    })
  ),
  imageVersion: PropTypes.string,
  fullscreenImageVersion: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default ImagesGallery;
